'use client';

import { useCallback, useEffect, useRef, useState } from 'react';
import { X } from 'lucide-react';
import {
  MARKETING_CONSENT_GIVEN_EVENT,
  OPEN_COOKIE_SETTINGS_EVENT,
  readMarketingConsent,
  readMarketingConsentInForce,
  saveMarketingConsent,
  type MarketingConsentChoice,
} from './marketing-consent';
import { withdrawMarketingConsent } from './withdraw-marketing-consent';

/** The line a reopened banner shows for the choice in effect. */
const CHOICE_SUMMARY: Record<MarketingConsentChoice, string> = {
  given: "You're allowing marketing cookies.",
  declined: "You've chosen essential cookies only.",
};

/**
 * Asks for Marketing Consent on a first visit, and reopens from Cookie
 * Settings (see CONTEXT.md) so the visitor can change their choice. Reopened,
 * it shows the current choice and can be closed without changing it; picking
 * the current choice just closes it. "Essential only" is a Withdrawal, which
 * reloads the page, whenever consent was given: stored, or in force on this
 * page, whose tags keep running after another tab withdraws.
 */
const CookieConsentBanner: React.FC = () => {
  const [isVisible, setIsVisible] = useState(false);
  const [choice, setChoice] = useState<MarketingConsentChoice | null>(null);
  // Bumped by each Cookie Settings click, so focus moves into the banner even
  // when it is already showing (a first visit).
  const [openRequests, setOpenRequests] = useState(0);
  const bannerRef = useRef<HTMLElement>(null);
  const returnFocusTo = useRef<HTMLElement | null>(null);
  // Whether this page loaded its Marketing Tags: consent was given when it
  // loaded, or given on it since. Another tab can change the cookie, not this.
  const tagsRunHere = useRef(false);

  useEffect(() => {
    const stored = readMarketingConsent();
    tagsRunHere.current = stored === 'given';
    setChoice(stored);
    setIsVisible(stored === null);

    const handleOpen = () => {
      returnFocusTo.current = document.activeElement instanceof HTMLElement ? document.activeElement : null;
      // Read again: the visitor may have changed it in another tab since this page loaded.
      setChoice((onThisPage) => readMarketingConsentInForce(onThisPage));
      setIsVisible(true);
      setOpenRequests((count) => count + 1);
    };

    window.addEventListener(OPEN_COOKIE_SETTINGS_EVENT, handleOpen);

    return () => {
      window.removeEventListener(OPEN_COOKIE_SETTINGS_EVENT, handleOpen);
    };
  }, []);

  useEffect(() => {
    if (openRequests > 0) bannerRef.current?.focus();
  }, [openRequests]);

  const close = useCallback(() => {
    setIsVisible(false);
    returnFocusTo.current?.focus();
    returnFocusTo.current = null;
  }, []);

  // Reopened over a choice already made: the choice to show, and the banner can be dismissed.
  const reopenedChoice = isVisible ? choice : null;

  useEffect(() => {
    if (reopenedChoice === null) return;

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') close();
    };

    document.addEventListener('keydown', handleKeyDown);

    return () => {
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [reopenedChoice, close]);

  const choose = (next: MarketingConsentChoice) => {
    // Given (read now: another tab may have given it since this banner
    // opened), or this page's tags still run under it: refusing is a Withdrawal.
    if (next === 'declined' && (readMarketingConsentInForce(choice) === 'given' || tagsRunHere.current)) {
      withdrawMarketingConsent();
      return;
    }

    // Saving the choice in effect again only renews it; the event is what
    // loads the tags here, even when another tab gave consent first.
    saveMarketingConsent(next);
    if (next === 'given') {
      tagsRunHere.current = true;
      window.dispatchEvent(new Event(MARKETING_CONSENT_GIVEN_EVENT));
    }
    setChoice(next);
    close();
  };

  if (!isVisible) {
    return null;
  }

  return (
    <aside
      ref={bannerRef}
      aria-label="Cookie choice"
      tabIndex={-1}
      data-page-overlay="cookie-banner"
      className="fixed inset-x-0 bottom-0 z-50 border-t border-base-300 bg-base-100/95 backdrop-blur focus:outline-none"
    >
      <div className="mx-auto flex max-w-container-lg flex-col gap-3 px-4 py-4 md:flex-row md:items-center md:justify-between">
        <div className="flex flex-col gap-1 text-sm">
          <p className="text-base-content/80">
            We use essential cookies to run this site and optional marketing cookies for advertising features.
            You can change this at any time from Cookie settings at the bottom of the page.
          </p>

          {reopenedChoice ? (
            <p className="font-medium">{CHOICE_SUMMARY[reopenedChoice]}</p>
          ) : null}
        </div>

        {/* `shrink-0`: beside the text (from `md`), the text wraps rather than the buttons overflowing. */}
        <div className="flex shrink-0 items-center gap-2">
          <button type="button" className="btn btn-ghost btn-sm" onClick={() => choose('declined')}>
            Essential only
          </button>

          <button type="button" className="btn btn-primary btn-sm" onClick={() => choose('given')}>
            Accept marketing
          </button>

          {reopenedChoice ? (
            <button type="button" className="btn btn-ghost btn-sm btn-square" aria-label="Close" onClick={close}>
              <X className="size-4" aria-hidden="true" />
            </button>
          ) : null}
        </div>
      </div>
    </aside>
  );
};

export default CookieConsentBanner;
